import { useMemo } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { BankingEntitiesPaymentStatusEnum } from 'generated/resources/graphql'

import { dateBackendDateTimeToRelativeTime, dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { VerticalTimeline } from 'modules/banking/components/VerticalTimeline'
import { statusLabelType, statusText } from 'modules/banking/lib/mappers'

type IconColor = 'grey600' | 'viridian800' | 'malibu1000' | 'radicalRed800'

interface BulkPayment {
  id: string
  label: string
  status: BankingEntitiesPaymentStatusEnum
  date: string
  amountCents?: number
  currency?: string
}

interface BulkPaymentsTimelineProps {
  payments: BulkPayment[]
  locale: string
  onPaymentClick?: (paymentId: string) => void
}

const getStatusIconColor = (status: BankingEntitiesPaymentStatusEnum): IconColor => {
  switch (status) {
    case 'paid':
    case 'done':
      return 'viridian800'
    case 'rejected':
    case 'rejected_consent':
      return 'radicalRed800'
    case 'in_progress':
    case 'pending_consent':
      return 'malibu1000'
    case 'init':
    case 'partially_paid':
    default:
      return 'grey600'
  }
}

const PaymentContent = ({ payment, locale }: { payment: BulkPayment; locale: string }) => {
  return (
    <Box flexDirection="column" gap="s4">
      <Text.Normal size="200" color="grey600">
        {dateBackendDateTimeToUI(payment.date)}
      </Text.Normal>
      {payment.amountCents !== undefined && payment.currency && (
        <Text.Normal size="200" color="grey700">
          {MoneyWithCurrency({ amount: payment.amountCents, currency: payment.currency, locale })}
        </Text.Normal>
      )}
    </Box>
  )
}

export const BulkPaymentsTimeline = ({
  payments,
  locale,
  onPaymentClick,
}: BulkPaymentsTimelineProps) => {
  // Sort by date descending (newest first)
  const sortedPayments = useMemo(() => {
    return [...payments].sort((a, b) => {
      const dateA = new Date(a.date).getTime()
      const dateB = new Date(b.date).getTime()
      return dateB - dateA
    })
  }, [payments])

  if (sortedPayments.length === 0) {
    return (
      <Box padding="s16">
        <Text.Normal size="200" color="grey600">
          {i18n.t('banking.paymentRequests.detail.noBulkPayments')}
        </Text.Normal>
      </Box>
    )
  }

  return (
    <Box width="full">
      <VerticalTimeline
        items={sortedPayments}
        getKey={(payment) => payment.id}
        getTitle={(payment) => payment.label}
        getStatusLabel={(payment) => statusText(payment.status)}
        getStatusType={(payment) => statusLabelType(payment.status)}
        getIconColor={(payment) => getStatusIconColor(payment.status)}
        getRelativeTime={(payment) => dateBackendDateTimeToRelativeTime(payment.date)}
        renderContent={(payment) => <PaymentContent payment={payment} locale={locale} />}
        onItemClick={onPaymentClick ? (payment) => onPaymentClick(payment.id) : undefined}
      />
    </Box>
  )
}
